import { generate } from "@ant-design/colors";
import { ArrowUpOutlined, UserOutlined } from "@ant-design/icons";
import {
  Button,
  Spin,
  Input,
  Layout,
  theme,
  Empty,
  Space,
  Tooltip,
  Avatar,
  GlobalToken,
  Row,
  Col,
} from "antd";
import React, { useState, useRef, useEffect } from "react";
import { MarkdownRenderer } from "src/components/MarkdownRenderer";
import { SparkleIcon } from "src/components/SparkleIcon";
import { VzblIcon } from "src/components/VzblIcon";
import { useApplicationContext } from "src/hooks/useApplication";
import { useColorScheme } from "src/hooks/useColorScheme";
import { SearchState } from "src/search/types";
import styles from "./Chat.module.css";
const { TextArea } = Input;

export interface Message {
  text: string;
  sender: "user" | "bot";
  prompts?: Array<{ title: string; prompt: string }>;
  copyEnabled?: boolean;
  isStreaming?: boolean;
}

export interface FollowUpPrompt {
  title: string;
  prompt: string;
}

export interface ResultSummaryResponse {
  summary: string;
  follow_up_prompts?: FollowUpPrompt[];
}

const neutralColors = (token: GlobalToken, colorScheme: string) => {
  return generate(token.colorBgLayout, {
    theme: colorScheme === "dark" ? "dark" : "default",
  });
};

const FollowUpPrompts = ({
  prompts,
  handleSend,
  className,
}: {
  prompts: FollowUpPrompt[];
  handleSend: (overridePrompt?: string) => Promise<void>;
  className?: string;
}) => {
  const { token } = theme.useToken();
  const colorScheme = useColorScheme();
  const neutral = neutralColors(token, colorScheme);
  return (
    <div className={className}>
      {prompts.map((prompt, index) => (
        <Tooltip title={prompt.title} key={index}>
          <Button
            type="dashed"
            style={{ border: 0, borderRadius: 20, backgroundColor: neutral[5] }}
            onClick={() => handleSend(prompt.prompt)}
          >
            <SparkleIcon style={{ marginRight: 5 }} /> {prompt.title}
          </Button>
        </Tooltip>
      ))}
    </div>
  );
};

const StreamingText = ({ text }: { text: string }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      // Handle different types of content differently
      const remainingText = text.slice(currentIndex);

      // Check for code blocks or table content to print in bulk
      const codeBlockMatch = remainingText.match(/^```[\S\s]*?```/);
      const tableMatch = remainingText.match(/^\|[\S\s]*?\n\n/);
      const listMatch = remainingText.match(/^[*-][\S\s]*?\n\n/);

      if (codeBlockMatch || tableMatch || listMatch) {
        const match = codeBlockMatch || tableMatch || listMatch;
        const chunk = match![0];
        const timeout = setTimeout(() => {
          setDisplayedText((prev) => prev + chunk);
          setCurrentIndex((prev) => prev + chunk.length);
        }, 100); // Slight pause before bulk printing
        return () => clearTimeout(timeout);
      }

      // Handle sentence-by-sentence for regular text
      const sentenceMatch = remainingText.match(/^[^\n!.?]+[\n!.?]/);
      if (sentenceMatch) {
        const sentence = sentenceMatch[0];
        // Stream each character in the sentence
        const timeout = setTimeout(() => {
          setDisplayedText((prev) => prev + sentence[0]);
          setCurrentIndex((prev) => prev + 1);
        }, 5); // Fast character streaming
        return () => clearTimeout(timeout);
      }

      // Default character-by-character streaming for remaining content
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + remainingText[0]);
        setCurrentIndex((prev) => prev + 1);
      }, 5);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text]);

  return <MarkdownRenderer>{displayedText}</MarkdownRenderer>;
};

export interface ChatProps {
  dataSources: Array<{ query: SearchState; format: "csv" }>;
  snippet?: SearchState;
  defaultPrompt?: string;
  startingPrompts?: String[];
  contextLabel?: React.ReactNode;
  inline?: boolean;
  title: string;
  handleSend: (
    prompt: string,
    messages: Message[],
    setMessages: (messages: Message[]) => void
  ) => Promise<void>;
  loading: boolean;
}

export const Chat = ({
  defaultPrompt,
  startingPrompts,
  contextLabel,
  inline = false,
  title,
  handleSend,
  loading,
}: ChatProps) => {
  const orgId = useApplicationContext().org_id!;
  const { token } = theme.useToken();
  const colorScheme = useColorScheme();
  const neutral = neutralColors(token, colorScheme);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState<string>("");
  const contentRef = useRef<HTMLDivElement>(null);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const isRunningPrompt = useRef(false);

  // Reset chat and run default prompt when topic changes
  useEffect(() => {
    if (
      defaultPrompt &&
      title &&
      !isRunningPrompt.current &&
      messages.length === 0
    ) {
      isRunningPrompt.current = true;

      // Reset messages
      const initialMessage = {
        text: `Tell me about ${title}`,
        sender: "user" as const,
        copyEnabled: false,
      };
      setMessages([initialMessage]);

      // Run the default prompt
      handleSend(defaultPrompt, [initialMessage], setMessages).finally(() => {
        isRunningPrompt.current = false;
      });
    }
  }, [defaultPrompt, title, handleSend, messages.length]);

  const onSendMessage = async () => {
    if (loading || !input.trim()) return;

    const userMessage = {
      text: input.trim(),
      sender: "user" as const,
      copyEnabled: false,
    };
    setMessages([...messages, userMessage]);
    setInput("");
    await handleSend(input.trim(), [...messages, userMessage], setMessages);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content} ref={contentRef}>
        {messages.map((message, index) => (
          <div key={index}>
            {message.sender === "user" ? (
              <div className={`${styles.message} ${styles.userMessage}`}>
                <div className={styles.messageIcon}>
                  <Avatar
                    size={30}
                    icon={<VzblIcon />}
                    style={{ backgroundColor: token.colorBgBase }}
                  />
                </div>
                <div
                  className={styles.messageText}
                  style={{ background: neutral[5] }}
                >
                  {message.text}
                </div>
              </div>
            ) : (
              <div className={styles.message}>
                <div className={styles.messageIcon}>
                  <Avatar
                    size={30}
                    style={{
                      backgroundColor: token.colorBgBase,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SparkleIcon
                      style={{ fontSize: 16, color: token.colorPrimary }}
                    />
                  </Avatar>
                </div>
                <div style={{ flex: 1 }}>
                  <div className={styles.botText}>
                    {message.isStreaming ? (
                      <StreamingText text={message.text} />
                    ) : (
                      <MarkdownRenderer>{message.text}</MarkdownRenderer>
                    )}
                  </div>
                  {message.prompts && message.prompts.length > 0 && (
                    <div className={styles.botPrompts}>
                      <FollowUpPrompts
                        prompts={message.prompts}
                        handleSend={onSendMessage}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        <div ref={endOfMessagesRef} />

        {loading && (
          <div className={styles.botMessage} style={{ opacity: 0.7 }}>
            <div className={styles.botIcon}>
              <Avatar
                size={30}
                style={{
                  backgroundColor: token.colorBgBase,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SparkleIcon
                  style={{ fontSize: 16, color: token.colorPrimary }}
                />
              </Avatar>
            </div>
            <div className={styles.botText}>
              <Spin size="small" style={{ marginRight: 10 }} />
              Thinking...
            </div>
          </div>
        )}
      </div>

      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          {contextLabel && (
            <div
              className={styles.contextLabel}
              style={{ color: token.colorTextSecondary }}
            >
              {contextLabel}
            </div>
          )}
          <div className={styles.textAreaContainer}>
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question about your data..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSendMessage();
                }
              }}
              className={styles.textArea}
              style={{
                borderRadius: token.borderRadiusLG,
                backgroundColor: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`,
              }}
            />
            <Button
              type="text"
              icon={<ArrowUpOutlined />}
              onClick={onSendMessage}
              loading={loading}
              className={styles.sendButton}
              style={{ color: token.colorPrimary }}
              disabled={!input.trim()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
